import axios from 'axios';

import getEnvVariable from 'utils/env';

export type GdvExportTab = 'key-indicators' | 'pos' | 'raw';

type ExportParamValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number>
  | [number | null, number | null];

export type GdvExportFilters = Record<string, ExportParamValue>;

const DEFAULT_FILE_NAME = 'export.xlsx';

// Lists are sent as repeated params (`market_id=1&market_id=2`), as FastAPI expects.
export function buildGdvExportQuery(
  filters: GdvExportFilters,
  columns: string[] = []
): URLSearchParams {
  const { rangePrices, ...rest } = filters;
  const params = new URLSearchParams();

  Object.entries(rest).forEach(([key, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
    } else {
      params.set(key, String(value));
    }
  });

  if (Array.isArray(rangePrices)) {
    const [min, max] = rangePrices;
    if (min != null) params.set('min_price', String(min));
    if (max != null) params.set('max_price', String(max));
  }
  columns.forEach((column) => params.append('columns', column));
  return params;
}

const getFileName = (contentDisposition?: string): string => {
  const match = contentDisposition?.match(
    /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i
  );
  return match ? decodeURIComponent(match[1]) : DEFAULT_FILE_NAME;
};

export async function downloadGdvExcel(
  tab: GdvExportTab,
  filters: GdvExportFilters,
  columns: string[] = []
): Promise<void> {
  // Lazy: the auth modules pull in the redux store, which tests mock out.
  const [{ getAuthToken }, { getAuthTokenAsClient }] = await Promise.all([
    import('actions/auth'),
    import('actions/connectAsClient')
  ]);
  const token = getAuthTokenAsClient() || getAuthToken();
  const baseUrl = getEnvVariable('REACT_APP_API_NEW_URL');
  const query = buildGdvExportQuery(filters, columns);

  const response = await axios.get(`${baseUrl}/v2/distribution/${tab}/xls`, {
    headers: { Authorization: `Token ${token}` },
    params: query,
    responseType: 'blob'
  });

  const objectUrl = URL.createObjectURL(
    new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
  );
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = getFileName(response.headers['content-disposition']);
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
